import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'

import { cn } from '@/lib/utils'

/**
 * Button — DESIGN.md
 *
 * variant
 *   primary   — main CTA (amber). default.
 *   secondary — neutral action (white surface + border).
 *   danger    — destructive action.
 *   outline   — bordered, transparent fill. used inside primary surfaces.
 *   ghost     — transparent, hover-only background. icon buttons, toggles.
 *   link      — text-only link.
 *
 * size
 *   sm     — h-8,  px-3,  text-sm
 *   md     — h-10, px-4,  text-sm  (default)
 *   lg     — h-12, px-6,  text-sm font-medium
 *   xl     — py-3.5 full-width CTA (height by padding)
 *   icon-* — square icon button (sm/md/lg)
 *
 * fullWidth — w-full helper.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-lg text-sm font-medium transition-colors',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg:not([class*=size-])]:size-4 [&_svg]:shrink-0',
    'outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground hover:bg-primary-hover',
        secondary:
          'bg-surface text-text border border-border-strong hover:bg-surface-muted dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700',
        danger:
          'bg-danger text-white hover:bg-red-600',
        outline:
          'border border-border bg-transparent text-text hover:bg-surface-muted dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800',
        ghost:
          'bg-transparent text-text hover:bg-surface-muted dark:text-gray-100 dark:hover:bg-gray-800',
        link:
          'text-primary underline-offset-4 hover:underline bg-transparent',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-sm font-medium',
        xl: 'px-4 py-3.5 text-sm font-bold',
        'icon-sm': 'size-8',
        'icon-md': 'size-10',
        'icon-lg': 'size-12',
        // Legacy aliases — keep working during refactor
        default: 'h-10 px-4 text-sm',
        xs: 'h-7 px-2 text-xs gap-1 [&_svg:not([class*=size-])]:size-3',
        icon: 'size-10',
        'icon-xs': 'size-7 [&_svg:not([class*=size-])]:size-3',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  },
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(function Button(
  { className, variant, size, fullWidth, asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, fullWidth, className }))}
      {...props}
    />
  )
})

export { Button, buttonVariants }
